import type { ExerciseKind } from '../domain/exercise';
import type { PendingPhoto } from './photo';
import type { SmAnswer } from './schemas';
import { AS_PERSON, parseScope, type WriteScope } from './scope';

/** What "Done" sends to Core (`useCompleteExercise`). */
export interface CompleteExerciseInput {
  kind: ExerciseKind;
  startedAt: string;
  completedAt: string;
  activeSeconds: number;
  source: 'workbook' | 'own' | 'other';
  sourceTitle?: string;
  /** The sheet or prompt used, when the workbook supplied it. */
  includesLineageUuid?: string;
  /** Quick math kept going past a sheet's end: the further sheets it went through. */
  alsoIncludesLineageUuids?: string[];
  answers?: SmAnswer[];
  /** Writing: the photo of the page, still on the device — uploaded before the attempt is saved. */
  photo?: PendingPhoto;
  /** Writing: the photo already in Core — the File content node's uuid the attempt links to. */
  photoFileUuid?: string;
  /** Who `photoFileUuid` was written as; the attempt must be written as the same. */
  scope?: WriteScope;
}

/**
 * Who to write an attempt as. Once its photo is in Core, as whoever wrote the photo: an attempt
 * can CONTAIN only what its own author can read. A photo uploaded by a build that predates
 * scopes was written as the person. Otherwise, `current`, the scope of the moment.
 */
export function attemptScope(input: CompleteExerciseInput, current: WriteScope): WriteScope {
  if (input.photoFileUuid) return parseScope(input.scope) ?? AS_PERSON;
  return current;
}

/**
 * A completed exercise that could not reach Core.
 * It waits on the device until a retry succeeds, so nothing the person did is lost. The
 * session uuid is *not* stored: today's session may be created by an earlier retry, so it
 * is looked up again when sending.
 */
export interface PendingAttempt {
  id: string;
  /** The local day the exercise was done on. */
  date: string;
  timeZone: string;
  input: CompleteExerciseInput;
  /** Who did the exercise (their OnEarly uuid); absent on attempts saved before it was recorded. */
  owner?: string;
  /** When Core recorded it; the attempt then counts locally until Core's history shows it. */
  sentAt?: number;
}

/** The attempts that are `person`'s to send: theirs, and those saved before owners were recorded. */
export function ownedBy(
  items: readonly PendingAttempt[],
  person: string | undefined,
): PendingAttempt[] {
  return items.filter((item) => item.owner === undefined || item.owner === person);
}

/** Where the app keeps the outbox between launches — a file on the device, `localStorage` on the web. */
export interface OutboxStorage {
  load(): string | null;
  save(serialised: string): void;
}

const KINDS: ReadonlySet<string> = new Set(['reading', 'writing', 'math']);

/** Whatever was stored, back into attempts — anything malformed is dropped rather than retried forever. */
export function parseOutbox(raw: string | null): PendingAttempt[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isPendingAttempt);
}

function isPendingAttempt(value: unknown): value is PendingAttempt {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Partial<PendingAttempt>;
  const input = item.input as Partial<CompleteExerciseInput> | undefined;
  return (
    typeof item.id === 'string' &&
    typeof item.date === 'string' &&
    typeof item.timeZone === 'string' &&
    typeof input === 'object' &&
    input !== null &&
    typeof input.kind === 'string' &&
    KINDS.has(input.kind) &&
    typeof input.startedAt === 'string' &&
    typeof input.completedAt === 'string' &&
    typeof input.activeSeconds === 'number'
  );
}

export function serialiseOutbox(items: readonly PendingAttempt[]): string {
  return JSON.stringify(items);
}

/**
 * Sends the pending attempts in order and returns what is still waiting: the first failure
 * stops the run (the network is most likely still away), and a rejected attempt — one Core
 * will never accept — is dropped so it cannot block the ones behind it.
 */
/**
 * Makes concurrent calls share one run: a call while one is under way gets that run's result
 * instead of starting another. The quiet retry and "Try again" can meet, and two runs sending
 * the same outbox would record an exercise twice.
 */
export function joinRunning<T>(): (run: () => Promise<T>) => Promise<T> {
  let running: Promise<T> | undefined;
  return (run) => {
    running ??= run().finally(() => {
      running = undefined;
    });
    return running;
  };
}

/**
 * The outbox as it is now after a flush: items Core rejected for good leave, items it recorded
 * stay marked with when (`recordedAt`), and what failed or arrived meanwhile is untouched.
 */
export function withoutProcessed(
  current: readonly PendingAttempt[],
  sent: readonly PendingAttempt[],
  remaining: readonly PendingAttempt[],
  recordedAt: ReadonlyMap<string, number> = new Map(),
): PendingAttempt[] {
  const processed = new Set(
    sent.filter((item) => !remaining.includes(item)).map((item) => item.id),
  );
  return current.flatMap((item) => {
    const at = recordedAt.get(item.id);
    if (at !== undefined) return [{ ...item, sentAt: at }];
    return processed.has(item.id) ? [] : [item];
  });
}
export async function flushOutbox(
  items: readonly PendingAttempt[],
  send: (item: PendingAttempt) => Promise<void>,
  retryable: (error: unknown) => boolean,
): Promise<{ remaining: PendingAttempt[]; sent: number; failed: boolean }> {
  const remaining = [...items];
  let sent = 0;
  while (remaining.length > 0) {
    const item = remaining[0] as PendingAttempt;
    try {
      await send(item);
      remaining.shift();
      sent += 1;
    } catch (error) {
      if (retryable(error)) return { remaining, sent, failed: true };
      remaining.shift();
    }
  }
  return { remaining, sent, failed: false };
}
